// Inline-expand variant of the variation form. Wraps VariationFormFields
// in write-mode inside an expanded register row. Auto-focuses the first
// editable input on mount; calls `onClosed` on unmount so the parent can
// restore focus to the row's toggle button.
//
// Critical: `number` and `status` are read-only here. The standard PUT
// path on the backend rejects both — number is sentinel-allocated on
// create only, and status changes go through approve / reject. Target
// membership and value impact ARE editable while the variation is
// `proposed`; the backend's status-gated PUT locks both post-decision,
// but the allocation split across the frozen targets stays editable —
// re-partitioning an already-approved total doesn't change what was
// approved.

import { useEffect, useRef, useState } from "react";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import VariationFormFields from "./VariationFormFields";
import VariationStatusPill from "./VariationStatusPill";
import VariesCell from "./VariesCell";
import ValueImpactCell from "./ValueImpactCell";
import {
  parseValueImpact,
  stateFromVariation,
  toUpdateVariationInput,
  type VariationFieldErrors,
  type VariationFormState,
} from "./variationFormState";
import { readTargets, validateAllocations } from "./variationTargets";
import { isValidIsoDate } from "../shared/isoDateValidation";
import type {
  Sow,
  UpdateVariationInput,
  Variation,
} from "../../../../types";

function validate(state: VariationFormState): VariationFieldErrors {
  const errors: VariationFieldErrors = {};
  if (!state.name.trim()) errors.name = "Name is required";
  // Effective date is optional on Proposed variations — the backend
  // only requires it at /approve time. Reject only a non-empty value
  // that fails ISO-date parsing; an empty string is acceptable on save.
  if (
    state.effectiveDate.trim().length > 0 &&
    !isValidIsoDate(state.effectiveDate)
  ) {
    errors.effectiveDate = "Enter a valid effective date";
  }
  let parsedValue: number | null = null;
  if (!state.nonFinancial) {
    parsedValue = parseValueImpact(state.valueImpact);
    if (parsedValue === null) {
      errors.valueImpact =
        "Enter a value, or tick Non-financial variation";
    } else if (parsedValue === 0) {
      errors.valueImpact =
        "Value cannot be zero. Use Non-financial for variations with no monetary effect.";
    }
  }
  // Allocation rules apply once the value is known. When the value is
  // missing or zero, the validateAllocations sum check is skipped — the
  // valueImpact error above takes priority and the user can't usefully
  // correct allocations until they fix the total.
  const allocationCheck = validateAllocations(
    state.targets,
    state.nonFinancial ? null : (parsedValue ?? 0),
  );
  if (allocationCheck) errors.targets = allocationCheck;
  return errors;
}

function hasErrors(errors: VariationFieldErrors): boolean {
  return Object.values(errors).some((v) => v !== undefined);
}

interface VariationInlineExpandProps {
  item: Variation;
  contractId: string;
  gstApplies: boolean | undefined;
  /** Used for the read-only Varies display — resolves SOW names from
   *  the target list. */
  sowsById: Map<string, Sow>;
  /** Full SOW list, surfaced into the target picker for proposed
   *  variations. May be empty if all SOWs have been archived. */
  sows?: Sow[];
  onUpdate: (input: UpdateVariationInput) => Promise<unknown>;
  onCancel: () => void;
  onClosed?: () => void;
  isSubmitting?: boolean;
}

export default function VariationInlineExpand({
  item,
  contractId,
  gstApplies,
  sowsById,
  sows = [],
  onUpdate,
  onCancel,
  onClosed,
  isSubmitting,
}: VariationInlineExpandProps) {
  const [state, setState] = useState<VariationFormState>(() =>
    stateFromVariation(item),
  );
  const [errors, setErrors] = useState<VariationFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const first = rootRef.current?.querySelector<HTMLElement>(
      'input:not([type="checkbox"]):not([type="radio"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
    );
    first?.focus();
    return () => {
      onClosed?.();
    };
  }, [onClosed]);

  const handleChange = (patch: Partial<VariationFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of keys) {
        delete next[k as keyof VariationFieldErrors];
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state);
    if (hasErrors(v)) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      await onUpdate(toUpdateVariationInput(state, contractId, item.version));
      onCancel();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save variation.",
      );
    }
  };

  return (
    <div ref={rootRef} className="p-4">
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-md bg-slate-50 dark:bg-slate-800/60 p-3">
        <div>
          <p className="text-caption text-slate-500 dark:text-slate-400">
            Number
          </p>
          <p className="text-body-sm font-mono text-slate-900 dark:text-slate-100">
            {item.number}
          </p>
        </div>
        <div>
          <p className="text-caption text-slate-500 dark:text-slate-400">
            Status
          </p>
          <p className="mt-0.5">
            <VariationStatusPill status={item.status} />
          </p>
        </div>
        <div>
          <p className="text-caption text-slate-500 dark:text-slate-400">
            Varies
          </p>
          <div className="mt-0.5">
            <VariesCell targets={readTargets(item)} sowsById={sowsById} />
          </div>
        </div>
        <div className="sm:col-span-3">
          <p className="text-caption text-slate-500 dark:text-slate-400">
            Value impact
          </p>
          <div className="mt-0.5">
            <ValueImpactCell
              value={item.valueImpact ?? null}
              gstApplies={gstApplies}
            />
          </div>
          <p
            data-testid="variation-inline-immutability-helper"
            className="text-caption text-slate-500 dark:text-slate-400 mt-1"
          >
            {item.status === "proposed"
              ? "Number and status cannot be edited here. Status transitions go through Approve / Reject."
              : "Number, status, target membership, and value impact cannot be edited here — the allocation split across the existing targets can still be adjusted. Raise a new variation to retract or replace, or use Correct for a data-entry typo (audit-logged)."}
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <VariationFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={`variation-edit-${item.id}`}
          mode="edit"
          gstApplies={gstApplies}
          sows={sows}
          disabled={isSubmitting}
          sourceStatus={item.status}
        />
        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}
